import React, { useState, useCallback } from 'react';
import { adminLogin } from '../api/admin.api';
import { useAdminAuth } from '../hooks/useAdminAuth';

type AdminLoginPageProps = {
  dark: boolean;
  onLoginSuccess: () => void;
  onBack: () => void;
};

export const AdminLoginPage = ({ dark, onLoginSuccess, onBack }: AdminLoginPageProps): React.JSX.Element => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [shake, setShake] = useState(false);

  const { login } = useAdminAuth();

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);
      setIsLoading(true);

      void (async () => {
        try {
          const response = await adminLogin({ email, password });
          login(response.data.token, response.data.user);
          onLoginSuccess();
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Błąd logowania');
          setShake(true);
          setTimeout(() => setShake(false), 500);
        } finally {
          setIsLoading(false);
        }
      })();
    },
    [email, password, login, onLoginSuccess]
  );

  return (
    <>
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
          20%, 40%, 60%, 80% { transform: translateX(5px); }
        }
        .shake { animation: shake 0.5s ease-in-out; }
      `}</style>

      <div
        className={`fixed inset-0 flex items-center justify-center transition-colors duration-500 ${
          dark
            ? 'bg-gradient-to-br from-gray-950 via-indigo-950 to-gray-950'
            : 'bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50'
        }`}
      >
        <button
          onClick={onBack}
          className={`fixed top-4 left-4 z-50 w-10 h-10 rounded-full flex items-center justify-center transition-all hover:scale-110 ${
            dark ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-700'
          } shadow-lg`}
        >
          ←
        </button>

        <div
          className={`w-full max-w-md mx-4 p-8 rounded-2xl shadow-2xl ${shake ? 'shake' : ''} ${
            dark ? 'bg-gray-900/90 border border-gray-800' : 'bg-white/90'
          }`}
        >
          <h1
            className={`text-2xl font-bold text-center mb-2 ${
              dark ? 'text-gray-100' : 'text-gray-800'
            }`}
          >
            Panel Admina
          </h1>
          <p className={`text-center mb-8 text-sm ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
            Zaloguj się, aby zarządzać wpisami
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label
                htmlFor="email"
                className={`block text-sm font-medium mb-2 ${
                  dark ? 'text-gray-300' : 'text-gray-700'
                }`}
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className={`w-full px-4 py-3 rounded-lg border transition-colors focus:outline-none focus:ring-2 ${
                  dark
                    ? 'bg-gray-800 border-gray-700 text-gray-100 focus:ring-indigo-500 focus:border-indigo-500'
                    : 'bg-white border-gray-300 text-gray-900 focus:ring-indigo-500 focus:border-indigo-500'
                }`}
                placeholder="admin@pamietniczek.pl"
              />
            </div>

            <div>
              <label
                htmlFor="password"
                className={`block text-sm font-medium mb-2 ${
                  dark ? 'text-gray-300' : 'text-gray-700'
                }`}
              >
                Hasło
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className={`w-full px-4 py-3 rounded-lg border transition-colors focus:outline-none focus:ring-2 ${
                  dark
                    ? 'bg-gray-800 border-gray-700 text-gray-100 focus:ring-indigo-500 focus:border-indigo-500'
                    : 'bg-white border-gray-300 text-gray-900 focus:ring-indigo-500 focus:border-indigo-500'
                }`}
                placeholder="••••••••"
              />
            </div>

            {error && (
              <div className="text-red-500 text-sm text-center bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className={`w-full py-3 px-4 rounded-lg font-medium transition-all ${
                isLoading
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:scale-[1.02] active:scale-[0.98]'
              } ${
                dark
                  ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                  : 'bg-indigo-500 hover:bg-indigo-600 text-white'
              }`}
            >
              {isLoading ? 'Logowanie...' : 'Zaloguj się'}
            </button>
          </form>
        </div>
      </div>
    </>
  );
};
